import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from "../Context/TenantContext";
import { useSubscription } from "../hooks/useSubscription";
import PremiumUpgradeModal from "../components/PremiumUpgradeModal";
import { canAccessBootCamp, getPremiumMessage } from "../utils/accessControl";
import API_BASE_URL from "../Config/config";

const BootcampJourneyPage = () => {
 const navigate = useNavigate();
 const { tenant } = useTenant();
 const { isPaid, isLoaded, plan } = useSubscription();
 const [isEnrolled, setIsEnrolled] = useState(false);
 const [loading, setLoading] = useState(true);

 // Check if this is a tenant student (college flow) - they should have access
 const isTenantStudent = (tenant && tenant.domain !== 'theskillgenie.com') || (plan === 'college');

 // Determine if user should be blocked
 const shouldBlockAccess = isLoaded && !canAccessBootCamp(isPaid) && !isTenantStudent;
 const upgradeMessage = getPremiumMessage('bootcamp');

 useEffect(() => {
   const checkEnrollment = async () => {
     const storedUser = localStorage.getItem('user');
     if (storedUser) {
       const user = JSON.parse(storedUser);
       try {
         const response = await fetch(`${API_BASE_URL}/bootcamp/enrollments/my-enrollment?userId=${user.id}`);
         if (response.ok) {
           const data = await response.json();
           if (data.ok && data.enrollment) {
             setIsEnrolled(true);
           }
         }
       } catch (err) {
         console.error("Error checking enrollment:", err);
       }
     }
     setLoading(false);
   };
   checkEnrollment();
 }, []);

 const handleEnrollClick = () => {
 navigate('/bootcamp1');
 };

 // Block rendering for free users (learner login)
 if (shouldBlockAccess) {
   return (
     <div className="min-h-screen bg-bgMain flex items-center justify-center px-4">
       <PremiumUpgradeModal
         isOpen={true}
         onClose={() => navigate("/dashboard")}
         title={upgradeMessage.title}
         message={upgradeMessage.message}
       />
     </div>
   );
 }

 return (
 <div className="min-h-screen bg-bgMain text-body px-6 py-12 md:px-16 font-sans">
 <div className="max-w-5xl mx-auto">
  <h1 className="text-4xl md:text-5xl font-extrabold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent text-center">
  ✨ Skill Genie Bootcamp Journey
  </h1>

  {isEnrolled && !loading && (
    <div className="bg-primary/10 border border-primary/20 p-4 rounded-xl text-center mb-8 animate-fade-in">
      <p className="text-primary font-bold">
        ✨ You are already enrolled in a bootcamp! 
        <button 
          onClick={() => navigate('/bootcamp1')} 
          className="ml-2 underline hover:text-secondary transition-colors"
        >
          View Details
        </button>
      </p>
    </div>
  )}

  <p className="text-lg text-muted text-center max-w-3xl mx-auto mb-10 leading-relaxed">
  Learn everything from <span className="text-primary font-bold">Database Design → Git → Dev → Agile → Deployment → Monitoring</span> — just like working in a real tech company.
  </p>

  <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 text-body p-6 rounded-2xl text-center font-semibold mb-12 shadow-2xl backdrop-blur-sm">
  <div className="flex items-center justify-center gap-2 mb-2">
  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">LIMITED SEATS</span>
  <p className="text-red-400 font-bold">Only 10 learners per batch</p>
  </div>
  <span className="text-primary">High-touch, real-team experience. No crowd. No fluff. Just serious results.</span><br />
  <p className="mt-2 text-muted">If you're ready to be treated like a professional from Day 1, <span className="text-primary font-bold">this is for you.</span></p>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
  <div className="space-y-6">
  <h2 className="text-2xl font-bold text-primary flex items-center gap-3">
  <span className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">🔧</span>
  Full Journey from Start to Production
  </h2>
 <div className="bg-bgSection rounded-3xl p-8 border border-accentGray/30 shadow-xl relative overflow-hidden group">
 <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl transition-all duration-500 group-hover:bg-primary/10"></div>
 <ul className="space-y-4 relative z-10">
 {[
 { title:"Requirement Analysis", desc:"Read PRD/BRD, derive use cases, create user stories" },
 { title:"System Design", desc:"Design DB schema, create ERD, define services & APIs" },
 { title:"Environment Setup", desc:"Set up Git repo, local dev tools, storyboards" },
 { title:"Development", desc:"Code frontend/backend, follow code guidelines, push to Git" },
 { title:"Agile Execution", desc:"Work in sprints, standups, retrospectives" },
 { title:"Testing", desc:"Write unit/integration tests, use Postman" },
 { title:"Deployment", desc:"CI/CD setup, AWS Lambda/Docker deploy" },
 { title:"Monitoring", desc:"Add logs, error tracking, CloudWatch basics" },
 { title:"Documentation", desc:"API docs (Swagger), README, handover notes" },
 { title:"Go Live & Support", desc:"Simulate production push, issue resolution" },
 ].map((item, i) => (
 <li key={i} className="flex gap-3">
 <span className="text-primary mt-1">•</span>
 <p className="text-body/90 text-sm">
 <strong className="text-primary">{item.title}:</strong> {item.desc}
 </p>
 </li>
 ))}
 </ul>
 </div>
 </div>

 <div className="space-y-6">
 <h2 className="text-2xl font-bold text-primary flex items-center gap-3">
 <span className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">🧾</span>
 Learner Deliverables
 </h2>
 <div className="bg-bgSection rounded-3xl p-8 border border-accentGray/30 shadow-xl h-full">
 <ul className="space-y-5">
 {[
"Commits in Git repo",
"DB schema submission",
"REST APIs implemented and deployed",
"CI/CD report (build -> deploy success logs)",
"QR-verified certificate + LinkedIn badge",
"Project experience + Git links",
 ].map((item, i) => (
 <li key={i} className="flex items-center gap-4 group">
 <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 group-hover:bg-green-500 group-hover:text-white transition-all">
 <span className="text-xs">✓</span>
 </div>
 <span className="text-body font-medium">{item}</span>
 </li>
 ))}
 </ul>

 {/* Hiring Note */}
 <div className="mt-12 bg-gradient-to-br from-primary to-secondary p-6 rounded-2xl shadow-2xl relative overflow-hidden group">
 <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
 <h3 className="text-xl font-bold mb-2 text-white flex items-center gap-2">
 <span>🌟</span> Exceptional Talent?
 </h3>
 <p className="text-white/90 text-sm leading-relaxed">
 Complete with flying colors, and you may be <span className="font-bold underline">invited to join our company</span>. We nurture skill - not just test it.
 </p>
 </div>
 </div>
 </div>
 </div>

 {/* ✅ Certificate Preview */}
 <div className="mt-20 text-center">
 <h2 className="text-3xl font-bold text-white mb-4">🎓 Certificate of Excellence</h2>
 <p className="text-muted mb-10 max-w-2xl mx-auto">
 Every graduate receives a <strong className="text-primary">QR-verifiable, employer-recognized certificate</strong> -
 proof of your end-to-end software delivery experience.
 </p>
 <div className="relative inline-block group">
 <div className="absolute -inset-1 bg-gradient-to-r from-primary to-secondary rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
 <img
 src="/skillgenie-certificate.jpg"
 alt="Skill Genie Certificate Preview"
 className="relative mx-auto shadow-2xl rounded-2xl border border-accentGray/50 max-w-lg w-full transition transform hover:scale-[1.02] duration-500"
 />
 </div>
 </div>

 {/* Quotation */}
 <blockquote className="italic text-muted text-center mt-20 max-w-4xl mx-auto text-xl leading-relaxed">
 <span className="text-primary text-4xl leading-none">“</span>
 Skill Genie Bootcamps simulate a real software development environment - guiding learners through the complete journey from requirement analysis to deployment and monitoring... to make them truly job-ready before the job.
 <span className="text-primary text-4xl leading-none">”</span>
 </blockquote>

 {/* Price Section */}
 <div className="mt-20 bg-bgSection rounded-3xl p-10 shadow-2xl border border-primary/20 text-center relative overflow-hidden">
 <div className="absolute inset-0 bg-primary/5 animate-pulse"></div>
 <div className="relative z-10">
 <h3 className="text-2xl font-bold text-white mb-2">💰 Complete Bootcamp Package</h3>
 <div className="text-4xl font-extrabold text-primary mb-4">₹2,499 <span className="text-sm text-muted font-normal">One-time investment</span></div>
 <p className="text-muted max-w-md mx-auto mb-10">
 Best value for those who want the <strong>full simulation experience</strong>. Invest once. Learn skills for a lifetime.
 </p>
 <button
onClick={handleEnrollClick}
className="btn-primary-gradient px-12 py-4 text-xl shadow-[0_0_40px_rgba(79, 70, 229,0.4)] hover:scale-105 active:scale-95 transition-all"
>
{isEnrolled ? "🚀 View My Enrollment" : "🚀 Enroll Now – Join the Top 10"}
</button>
 </div>
 </div>
 </div>
 </div>
 );
};

export default BootcampJourneyPage;




